import { Plus } from 'lucide-react'
import { Button, CompanyList, CompanyForm, CompanyFilters } from '../components'
import { useCompanies } from '../hooks/useCompanies'
import { useAuth } from '../contexts/AuthContextTest'

function Companies() {
  const { user } = useAuth()
  const {
    // State
    searchTerm,
    setSearchTerm,
    currentPage,
    showModal,
    setShowModal,
    editingCompany,
    selectedLogo,
    logoPreview,
    
    // Data
    companies,
    isLoading,
    totalPages,
    totalItems,
    
    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,
    isUploadingLogo,
    isDeletingLogo,
    
    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handleLogoSelect,
    handleLogoUpload,
    handleLogoDelete,
    handleRemoveLogoPreview,
    handlePageChange,
    handleClearFilters,
  } = useCompanies({}, user)

  // Check if user can create companies
  const canCreateCompany = user?.role?.id === 1 // Only Super Admin can create companies

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">
            {user?.role?.id === 2 ? 'My Company' : 'Companies'}
          </h1>
          <p className="mt-1 text-base text-secondary-600">
            {user?.role?.id === 2 
              ? 'Manage your company information' 
              : 'Manage multi-tenant companies'
            }
          </p>
        </div>
        {canCreateCompany && (
          <Button
            onClick={() => setShowModal(true)}
            variant="primary"
            size="md"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        )}
      </div>

      {/* Filters */}
      <CompanyFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearFilters={handleClearFilters}
      />

      {/* Companies List */}
      <CompanyList
        companies={companies}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onLogoSelect={handleLogoSelect}
        onLogoDelete={handleLogoDelete}
        isUploadingLogo={isUploadingLogo}
        isDeletingLogo={isDeletingLogo}
        userRole={user?.role?.id}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card">
          <div className="card-content">
            <div className="flex items-center justify-between">
              <div className="text-sm text-secondary-700">
                Showing {((currentPageData - 1) * 10) + 1} to {Math.min(currentPageData * 10, totalItems)} of {totalItems} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-secondary-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Form Modal */}
      <CompanyForm
        isOpen={showModal}
        editingCompany={editingCompany}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
        selectedLogo={selectedLogo}
        logoPreview={logoPreview}
        onLogoSelect={(e) => handleLogoSelect(e)}
        onLogoUpload={handleLogoUpload}
        onLogoDelete={handleLogoDelete}
        onRemoveLogoPreview={handleRemoveLogoPreview}
        isUploadingLogo={isUploadingLogo}
      />
    </div>
  )
}

export default Companies
